"use client";

import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { FunctionArgs } from "convex/server";
import type { DiscoveryProduct } from "@/components/discovery/types";
import type { HeroLocation } from "@/components/home/context/location-context";
import { toDiscoveryProduct } from "@/components/listing/browse/marketplace-listing";

export const NEAR_YOU_REACH_MILES = 500;
const ROW_LIMIT = 8;

interface NearYouProductsArgs {
  location: HeroLocation | null;
  isSeeding: boolean;
}

interface NearYouProducts {
  products: DiscoveryProduct[];
  isLoading: boolean;
}

function nearbyArgs(
  location: HeroLocation,
): FunctionArgs<typeof api.products.nearbyQueries.getSmartNearbyWithFreight> {
  return {
    buyerLat: location.lat,
    buyerLon: location.lng,
    buyerZip: location.postalCode,
    buyerState: location.state,
    initialRadiusMiles: NEAR_YOU_REACH_MILES,
    limit: ROW_LIMIT,
    inStock: true,
    intent: "sell",
  };
}

function nationwideArgs(): FunctionArgs<typeof api.products.marketplaceQueries.listMarketplacePage> {
  return {
    page: 1,
    perPage: ROW_LIMIT,
    inStock: true,
    sortBy: "newest",
    intent: "sell",
  };
}

export function useNearYouProducts({
  location,
  isSeeding,
}: NearYouProductsArgs): NearYouProducts {
  const nearby = useQuery(
    api.products.nearbyQueries.getSmartNearbyWithFreight,
    location ? nearbyArgs(location) : "skip",
  );
  const nationwide = useQuery(
    api.products.marketplaceQueries.listMarketplacePage,
    location || isSeeding ? "skip" : nationwideArgs(),
  );

  if (location) {
    return {
      products: nearby?.products ?? [],
      isLoading: nearby === undefined,
    };
  }
  return {
    products: (nationwide?.listings ?? []).map(toDiscoveryProduct),
    isLoading: isSeeding || nationwide === undefined,
  };
}
